import * as React from "react";
import EditViewCard, { EditViewDisplayMode } from "../../components/common/EditViewCard"
import * as _ from "lodash";
import IEditedBy from "../../models/IEditedBy";
import ValidationUtils from "../../../../common/utils/ValidationUtils";
import { FormItemMode, QuestionType, SchemaType, AppConstants } from "../../utils/AppConstants";
import ITreatmentPlanTemplateResponseObj, { ITemplateInfo } from "../../models/ITreatmentPlanTemplateResponse";
import IAppointmentSchedule from "../../models/IAppointmentSchedule";


export interface GenericEditViewContProps {
    displayMode: EditViewDisplayMode,
    appointmentId?: number,
    editedBy?: IEditedBy,
    responseStatus?: any,
    patientId?: number,
    widgetData: any,
    tabData: any,
    onUndoApplyTreatmentPlan?: (callback: Function, removedTemplate?: ITemplateInfo) => void;
    doctorName?: string,
    fetchPatientPastSessions?: (patientId: number, subSectionKey: string) => Promise<any>,
    subSectionTitle?: string,
    updateHandler?: (updatedObject: any) => void,
    className?: string,
    modalHeaders?: [],
    addSymptomForReview?: (name: string) => Promise<any>,
    responseData?: any
    notifyParent?: (param?: any) => void
    modalResponse?: any
    suggestions?: { [key: string]: any }
    isNew?: boolean
    onTreatmentPlanSelect: (template: ITreatmentPlanTemplateResponseObj) => void
    fetchDataOnSelect: (diagnosis?: Array<string>) => void
    savePatientConcern?: () => void
    cancelActionHandler?: () => void
    actionButtonText?: { action: string, primaryAction: string }
    emptyText?: string
    appointmentSchedule: IAppointmentSchedule
    getSuggestionsSequentially?: () => void;
}

export interface GenericEditViewContState {
    updatedObject: any;
    validations: any
}

class GenericEditViewCont extends React.Component<GenericEditViewContProps, GenericEditViewContState> {
    constructor(props: GenericEditViewContProps) {
        super(props);
        this.state = {
            updatedObject: this.getUpdatedObject(props),
            validations: null
        };
    }

    componentDidUpdate(PrevProps: GenericEditViewContProps) {
        if (!_.isEqual(PrevProps.tabData, this.props.tabData))
            this.setState({ updatedObject: this.getUpdatedObject(this.props) })
    }

    getUpdatedObject = (props: GenericEditViewContProps) => {
        let updatedObject = { ...props.tabData };
        if (props.isNew) {
            const templatedListKeys = this.getTemplatedListKeys();
            templatedListKeys.map((templateItem: { key: string, openMode: boolean }) => {
                if (!_.isEmpty(updatedObject[templateItem.key])) {
                    updatedObject[templateItem.key] = updatedObject[templateItem.key].map((item: any) => ({ item, mode: templateItem.openMode ? FormItemMode.EDIT : FormItemMode.VIEW }))
                }
            });
        }
        return updatedObject
    }

    getTemplatedListKeys = (): { key: string, openMode: boolean }[] => {
        const configQuestions = this.props.widgetData && !_.isEmpty(this.props.widgetData.data) ? this.props.widgetData.data : null;
        if (configQuestions) {
            const templatedListQuestions = (configQuestions || []).filter((question: any) => {
                return !_.isEmpty(question.answerList) && question.answerList[0].type === QuestionType.TEMPLATED_LIST_V2
            });
            return templatedListQuestions.map((question: any) => ({ key: question.key, openMode: question.answerList[0].openMode }));
        }
        return [];
    }

    isDataEmpty = (): boolean => {
        const updatedObject = { ...this.state.updatedObject };
        const data: any = _.omit(updatedObject, ["referenceId"]);
        for (const key in data) {
            if (!_.isEmpty(data[key])) {
                return false;
            }
        }
        return true;
    }

    getSaveObject = (): any[] => {
        let updatedObject = Object.assign({}, this.state.updatedObject);
        let templatedListKeys = this.getTemplatedListKeys();
        if (!_.isEmpty(templatedListKeys)) {
            let dataToBeSaved: any = _.omit(updatedObject, ["referenceId"]);
            templatedListKeys.forEach((templateItem: { key: string, openMode: boolean }) => {
                if (!_.isEmpty(dataToBeSaved[templateItem.key])) {
                    dataToBeSaved[templateItem.key] = dataToBeSaved[templateItem.key].map((dataItem: any) => dataItem.item);
                }
            })
            return { ...updatedObject, ...dataToBeSaved };
        }
        return updatedObject;
    }

    isFormValid = (schema: any, updatedObject: any, validations: any): boolean => {
        let result = true;
        if (schema.type === SchemaType.OBJECT) {
            let propList = Object.keys(schema.properties);
            for (let propKey of propList) {
                let newSchema = { ...schema.properties[propKey] };
                validations[propKey] = newSchema.type === SchemaType.ARRAY ? [] : {};
                if (schema.required && schema.required.length > 0 && schema.required.includes(propKey)) {
                    newSchema["required"] = true;
                }
                let obj = updatedObject && updatedObject[propKey] ? updatedObject[propKey] : "";
                result = result && this.isFormValid(newSchema, obj, validations[propKey]);
            }
        } else if (schema.type === SchemaType.ARRAY && updatedObject) {
            let newSchema = { ...schema.items };
            if (updatedObject && updatedObject.length > 0) {
                for (let key in updatedObject) {
                    validations[key] = newSchema.type === SchemaType.ARRAY ? [] : {};
                    result = result && this.isFormValid(newSchema, updatedObject[key], validations[key]);
                }
            } else {
                result = result && this.isFormValid(newSchema, [], {});
            }
        } else {
            let validationResult = ValidationUtils.checkValidationType(updatedObject, schema);
            Object.assign(validations, validationResult);
            result = result && validationResult.result;
        }
        return result;
    }

    isFormDirty = () => {
        // TODO: check the object against validators to detect error
        // let { validationSchema } = this.props.widgetData;
        // let { updatedObject } = { ...this.state };
        // let validations = {};
        // let isFormDirty = !(this.isFormValid(validationSchema, updatedObject, validations));
        // this.validationHandler(validations);
        // return isFormDirty;
        return false;
    }

    updateHandler = (updatedObject: any) => {
        this.setState({ updatedObject });
    }

    validationHandler = (validations: any) => {
        this.setState({ validations: validations });
    }

    onTreatmentPlanSelect = (template: ITreatmentPlanTemplateResponseObj, updatedObj: any, key: string) => {
        let updatedObject = { ...this.state.updatedObject, [key]: updatedObj };
        this.setState({ updatedObject },
            () => {
                this.props.onTreatmentPlanSelect(template)
            });
    }

    getGenericEditViewTemplateProps = () => {
        let GenericEditProps: any = {
            widgetData: { ...this.props.widgetData },
            patientId: this.props.patientId,
            fetchPatientPastSessions: this.props.fetchPatientPastSessions,
            updatedObject: {
                ...this.state.updatedObject,
                diagnosticData: this.props.tabData.diagnosticData,
                complaints: this.props.tabData.complaints
            },
            updateHandler: this.updateHandler,
            validationHandler: this.validationHandler,
            validations: this.state.validations,
            checkData: this.props.widgetData,
            className: this.props.className,
            modalHeaders: this.props.modalHeaders,
            addSymptomForReview: this.props.addSymptomForReview,
            responseData: this.props.responseData,
            modalResponse: this.props.modalResponse,
            suggestions: this.props.suggestions,
            onTreatmentPlanSelect: this.onTreatmentPlanSelect,
            onUndoApplyTreatmentPlan: this.props.onUndoApplyTreatmentPlan,
            fetchDataOnSelect: this.props.fetchDataOnSelect,
            emptyText: this.props.emptyText,
            appointmentSchedule: this.props.appointmentSchedule,
            getSuggestionsSequentially: this.props.getSuggestionsSequentially
        }
        return { "view": GenericEditProps, "edit": GenericEditProps };
    }

    savePatientConcern = () => {
        this.props.savePatientConcern && this.props.savePatientConcern()
    }

    cancelActionHandler = () => {
        this.props.cancelActionHandler && this.props.cancelActionHandler()
    }

    hasEditModeItem = () => {
        let hasEditItem = false;
        let templatedListKeys = this.getTemplatedListKeys();
        if (!_.isEmpty(templatedListKeys)) {
            let dataToBeSaved: any = _.omit(this.state.updatedObject, ["referenceId"]);
            templatedListKeys.forEach((templateItem: { key: string, openMode: boolean }) => {
                if (!_.isEmpty(dataToBeSaved[templateItem.key])) {
                    const editDataSet = dataToBeSaved[templateItem.key].filter((dataItem: { item: any, mode: FormItemMode }) => !templateItem.openMode && dataItem.mode === FormItemMode.EDIT);
                    if (editDataSet.length > 0) hasEditItem = true;
                }
            });
        }
        return hasEditItem;
    }

    render() {
        const title = _.get(this.props, "widgetData.subSectionTitle", "");
        const editViewCardProps = this.getGenericEditViewTemplateProps();
        return (
            <EditViewCard
                cardTitleDisplay={title}
                templateName={"GenericEditor"}
                templateProps={editViewCardProps}
                lastEditedEpoch={new Date().getTime()}
                saveActionHandler={this.savePatientConcern}
                cancelActionHandler={this.cancelActionHandler}
                displayMode={this.props.displayMode}
                collapseInEdit={true}
                actionButtonText={this.props.actionButtonText}
                collapsedTextPlaceholderForMobile={AppConstants.SUBSECTION_ADD_PLACEHOLDERS[this.props.widgetData && this.props.widgetData.subSectionKey]}
            />
        )
    }
}

export default GenericEditViewCont;